import React, { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_REACTIONS } from '../../graphql/operations';
import ReactionPicker from './ReactionPicker';
import { normalizeReactionToken, toReactionEmoji } from './reactionEmojiMap';
import { parseReactionCounts } from './reactionCounts';
import { useReactionToggle } from './useReactionToggle';
import './ReactionBar.css';

/**
 * ReactionBar — renders reaction pills below a message.
 *
 * Each pill shows emoji + count. Hover reveals who reacted (lazy-loaded).
 * Click toggles the reaction via the shared `useReactionToggle` hook (the
 * same hook the hover-toolbar's quick-react chip uses — so pill and chip
 * cannot drift). A "+" button opens the ReactionPicker popover.
 */
function ReactionBar({ reactionCounts: rawReactionCounts, messageId, conversationId, currentUserId, onReactionCountsChange }) {
  const [showPicker, setShowPicker] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, emoji: null, names: [] });

  // Tooltip-only lazy query (kept separate from the hook's query because
  // its purpose is to read the reactor list for the hover label).
  const [fetchReactionsForTooltip] = useLazyQuery(GET_REACTIONS);

  // Defensive parse — see reactionCounts.js for the AWSJSON shape rationale.
  const reactionCounts = parseReactionCounts(rawReactionCounts);

  // The hook needs a setter that takes a NEW counts object (not the
  // functional updater form ReactionBar previously used). Wrap so the
  // upstream `onReactionCountsChange` keeps its existing signature.
  const setReactionCounts = useCallback(
    (next) => {
      if (typeof onReactionCountsChange === 'function') {
        onReactionCountsChange(next);
      }
    },
    [onReactionCountsChange]
  );

  const handleToggle = useReactionToggle({
    messageId,
    conversationId,
    currentUserId,
    reactionCounts,
    setReactionCounts
  });

  if (!reactionCounts || Object.keys(reactionCounts).length === 0) return null;

  const handleHoverPill = async (emojiToken) => {
    try {
      const result = await fetchReactionsForTooltip({ variables: { messageId } });
      const reactions = result?.data?.getReactions || [];
      const names = reactions
        .filter((r) => normalizeReactionToken(r.emoji) === emojiToken)
        .map((r) => r.displayName || r.userId);
      setTooltip({ visible: true, emoji: emojiToken, names });
    } catch {
      // Silently ignore tooltip fetch errors
    }
  };

  const handleLeavePill = () => {
    setTooltip({ visible: false, emoji: null, names: [] });
  };

  return (
    <div className="reaction-bar">
      {Object.entries(reactionCounts).map(([emojiValue, count]) => {
        const emojiToken = normalizeReactionToken(emojiValue);
        const countLabel = count === 1 ? '1 reaction' : `${count} reactions`;
        return (
          <button
            key={emojiValue}
            type="button"
            className="reaction-pill"
            aria-label={`${countLabel} for ${emojiToken}`}
            onClick={() => handleToggle(emojiToken)}
            onMouseEnter={() => handleHoverPill(emojiToken)}
            onMouseLeave={handleLeavePill}
            onFocus={() => handleHoverPill(emojiToken)}
            onBlur={handleLeavePill}
          >
            <span className="reaction-pill-emoji" aria-hidden="true">{toReactionEmoji(emojiValue)}</span>
            <span className="reaction-pill-count">{count}</span>
            {tooltip.visible && tooltip.emoji === emojiToken && tooltip.names.length > 0 && (
              <span className="reaction-tooltip" role="tooltip">{tooltip.names.join(', ')}</span>
            )}
          </button>
        );
      })}
      <div className="reaction-add-wrapper">
        <button
          type="button"
          className="reaction-add-btn"
          aria-label="Add reaction"
          title="Add reaction"
          onClick={() => setShowPicker((v) => !v)}
        >
          +
        </button>
        {showPicker && (
          <ReactionPicker
            messageId={messageId}
            conversationId={conversationId}
            onSelect={(emojiToken) => {
              // The picker already fired AddReaction; bump local state so the
              // pill renders without waiting on the subscription. Uses the
              // functional setter form so concurrent updates compose. If the
              // viewer had already reacted with this emoji, this can briefly
              // show a phantom +1 until the next refetch — same edge case as
              // before the `useReactionToggle` extraction; a follow-up could
              // have the picker propagate the server's returned counts.
              if (typeof onReactionCountsChange !== 'function') return;
              onReactionCountsChange((prev) => {
                const next = { ...(parseReactionCounts(prev) || {}) };
                next[emojiToken] = (next[emojiToken] || 0) + 1;
                return next;
              });
            }}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>
    </div>
  );
}

export default ReactionBar;
